import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { firebase } from './firebase.js';
import { readEnv } from './env.js';
import { removeFcmToken, saveFcmToken } from './repo.js';
import { isNative, platform } from './device.js';

/**
 * Reminders (docs/PRODUCT.md §6).
 *
 * Every platform is push: the `scheduleReminders` function decides who is due
 * (local hour, weekday, days since the last session) and sends through FCM.
 * The device only registers a token. Native uses @capacitor-firebase/messaging
 * (APNs on iOS, FCM on Android); the web uses FCM web push with a VAPID key.
 * The schedule itself lives in `users/{uid}.settings`.
 *
 * Capacitor plugins are imported dynamically so the web bundle never loads them.
 */
export type ReminderPermission = 'granted' | 'denied' | 'unsupported' | 'default';

/** Android channel for reminders. Must match `scheduleReminders` and the manifest default. */
export const REMINDER_CHANNEL = 'reminders';

/** Where a tapped reminder lands when the payload does not say. Home, never the camera. */
const DEFAULT_LINK = '/progress';

/** This device's last registered token, so sign-out can unregister exactly it. */
const TOKEN_KEY = 'erectus.fcmToken';

function rememberToken(token: string): void {
  try {
    window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Storage unavailable: sign-out will leave the token, and the server prunes
    // it once FCM reports it unregistered.
  }
}

function rememberedToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export async function reminderPermission(): Promise<ReminderPermission> {
  if (isNative()) {
    try {
      const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
      const { receive } = await FirebaseMessaging.checkPermissions();
      if (receive === 'granted') return 'granted';
      if (receive === 'denied') return 'denied';
      return 'default';
    } catch {
      return 'unsupported';
    }
  }
  if (typeof Notification === 'undefined') return 'unsupported';
  if (!(await isSupported().catch(() => false))) return 'unsupported';
  return Notification.permission as ReminderPermission;
}

/** Ask for notification permission (the only place that prompts) and register this device. */
export async function enableReminders(uid: string): Promise<ReminderPermission> {
  return isNative() ? enableNativePush(uid) : enableWebPush(uid);
}

async function enableNativePush(uid: string): Promise<ReminderPermission> {
  try {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    const { receive } = await FirebaseMessaging.requestPermissions();
    if (receive === 'denied') return 'denied';
    if (receive !== 'granted') return 'default';
    await registerNativeToken(uid);
    return 'granted';
  } catch {
    return 'unsupported';
  }
}

async function registerNativeToken(uid: string): Promise<void> {
  const { FirebaseMessaging, Importance, Visibility } = await import('@capacitor-firebase/messaging');
  if (platform() === 'android') {
    await FirebaseMessaging.createChannel({
      id: REMINDER_CHANNEL,
      name: 'Reminders',
      description: 'Your weekly posture check-in',
      importance: Importance.Default,
      visibility: Visibility.Private,
    });
  }
  const { token } = await FirebaseMessaging.getToken();
  if (!token) return;
  await saveFcmToken(uid, token);
  rememberToken(token);
}

async function enableWebPush(uid: string): Promise<ReminderPermission> {
  const supported = await reminderPermission();
  if (supported === 'unsupported') return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission as ReminderPermission;

  const vapidKey = readEnv().vapidKey;
  if (!vapidKey) return 'unsupported';
  try {
    const messaging = getMessaging(firebase().app);
    const token = await getToken(messaging, { vapidKey });
    if (token) {
      await saveFcmToken(uid, token);
      rememberToken(token);
    }
    return 'granted';
  } catch {
    return 'denied';
  }
}

/**
 * Re-register on every native launch with a signed-in user: FCM rotates tokens,
 * and a reinstall issues a new one. Never prompts — permission is only asked
 * from Settings.
 */
export async function refreshPushToken(uid: string): Promise<void> {
  if (!isNative()) return;
  if ((await reminderPermission()) !== 'granted') return;
  await registerNativeToken(uid).catch(() => undefined);
}

/** Before sign-out, while the rules still let us write the user doc. */
export async function forgetPushToken(uid: string): Promise<void> {
  const token = rememberedToken();
  if (token) await removeFcmToken(uid, token).catch(() => undefined);
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Nothing to clear.
  }
  if (isNative()) {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    await FirebaseMessaging.deleteToken().catch(() => undefined);
  }
}

/**
 * Route a tapped reminder into the app. Native only: on the web the service
 * worker opens `fcmOptions.link` itself.
 */
export async function listenForReminderTaps(onOpen: (link: string) => void): Promise<void> {
  if (!isNative()) return;
  const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
  await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
    const data = (event.notification.data ?? {}) as { link?: unknown };
    const link = typeof data.link === 'string' && data.link.startsWith('/') ? data.link : DEFAULT_LINK;
    onOpen(link);
  });
}

/** The next local time a reminder on `days` at `hour` fires, after `from`. */
export function nextOccurrence(days: readonly number[], hour: number, from = new Date()): Date | null {
  if (days.length === 0) return null;
  for (let ahead = 0; ahead <= 7; ahead++) {
    const candidate = new Date(from.getFullYear(), from.getMonth(), from.getDate() + ahead, hour);
    if (candidate > from && days.includes(candidate.getDay())) return candidate;
  }
  return null;
}

export const DOW_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
